import { createContext, useContext } from 'react';
import type { JSX, ReactNode } from 'react';
import type { Cell } from '../views/contract.js';
/* LES DEUX GESTES DE CHEMIN DES MODELES, importes et non recopies une fois de
   plus : `lib/templates.tsx` n'importe que des TYPES et n'importe jamais ce
   fichier, donc l'import de valeur ne ferme aucun cycle. */
import { lireChemin, poserChemin } from './templates.js';

/**
 * LA CREATION EN FICHE, cote SOCLE : le PORT, la fiche vide, et les regles
 * pures du brouillon. Rien d'autre.
 *
 * CE QUE CE FICHIER NE FAIT PAS, ET POURQUOI. Il n'importe pas `@taki/sdk` et
 * il n'en importera jamais : l'ordre de build est sdk -> ui -> shell -> apps,
 * et le socle d'interface ne peut pas dependre de ce qui est construit apres
 * lui. C'est le meme partage que les modeles (`lib/templates.tsx`) et les
 * liens (`lib/liens.tsx`) : le socle decrit la FORME du service (un verbe), et
 * l'HOTE la remplit avec le repository reel (`sdk.createIn`).
 *
 * LA DECISION QUI FONDE CE FICHIER (fondateur, 09/09/2026) : « + Ajouter »
 * n'ouvre plus une fenetre `Modal` ecrite par chaque app. Il ouvre, en zone 5,
 * une FICHE VIDE, projetee par le binding ORDINAIRE de la porte, dont les
 * saisies restent EN MEMOIRE jusqu'au clic sur « Creer ». Comme dans Odoo. Le
 * moteur (`views/useScreen.ts`) tient ce brouillon ; le chassis
 * (`components/ScreenLayout.tsx`) rend les deux boutons et la fenetre de
 * sortie ; la fiche (`views/core/RecordView.tsx`) rend les placeholders. Ici ne
 * vit que ce que les trois partagent, et ce qui se prouve sans React.
 */

/**
 * L'ID DE LA FICHE EN COURS DE CREATION, dans l'etat comme dans l'adresse
 * (`?id=~nouveau`).
 *
 * POURQUOI UN TILDE. Un id Firestore genere ne commence jamais par `~` : aucune
 * fiche reelle ne peut le porter, donc l'ouvrir ne peut jamais montrer un
 * brouillon a la place d'une fiche qui existe. Et le caractere passe dans une
 * adresse sans encodage : « ~nouveau » se lit tel quel dans la barre.
 *
 * L'ADRESSE NE PORTE QUE CET ID, jamais les valeurs saisies : on y tape des
 * noms, et une adresse se copie. F5 rouvre donc un brouillon VIDE, et c'est
 * voulu : un lien copie promet une creation, pas une saisie.
 */
export const BROUILLON_ID = '~nouveau';

/** Le sac ou vit un champ SUR-MESURE, redit ici pour ne pas importer l'assemblage. */
const SAC = 'customFields.';

/**
 * CE QUE L'HOTE FOURNIT : un seul verbe. `family` est le repository du SDK
 * (au PLURIEL : `assets`, `careRecords`), `type` le sous-type que la porte
 * montre (`materiel`), `fields` les champs de la fiche tels que le brouillon
 * les a ranges (racine et `customFields`), SANS `id` ni `type`.
 *
 * L'hote y pose `orgId` et `type`, puis appelle le repository : c'est lui qui
 * sait dans quelle org il tourne, le socle ne le devine pas.
 */
export interface CreationPort {
  create(input: {
    family: string;
    type: string;
    fields: Record<string, unknown>;
  }): Promise<{ id: string }>;
}

/**
 * LE CONTEXTE, DEFAUT `null`, et ce defaut est LU. Un port absent est un fait
 * ordinaire (une app montee sans hote, un test, un shell sans SDK) : il grise
 * « Creer » et l'action d'ajout avec leur raison, il ne casse rien.
 */
const CreationContext = createContext<CreationPort | null>(null);

/**
 * Fournisseur pose par l'HOTE (celui qui a le SDK), autour du shell. Meme patron
 * que `ModelesProvider` : le socle decrit, l'exterieur remplit.
 */
export function CreationProvider({
  value,
  children,
}: {
  value: CreationPort | null;
  children: ReactNode;
}): JSX.Element {
  return <CreationContext.Provider value={value}>{children}</CreationContext.Provider>;
}

/** Le port de creation, ou `null` quand l'hote n'en fournit pas. */
export function useCreationPort(): CreationPort | null {
  return useContext(CreationContext);
}

/**
 * LES RAISONS QUI ETEIGNENT LA CREATION, ecrites ici et nulle part ailleurs :
 * le moteur les pose sur « + Ajouter » et sur « Creer », le chassis les rend
 * (G16 : un controle eteint dit pourquoi).
 */
export const RAISONS_CREATION = {
  /** Personne n'a monte de `CreationProvider` au-dessus de l'ecran. */
  sansPort: "L'hote ne fournit pas la creation de fiches.",
  /** La porte ne declare pas `jeu` : on ne sait ni la famille ni le sous-type. */
  sansJeu: 'Cette porte ne declare pas son jeu : on ne sait pas ou creer la fiche.',
  /** La porte n'a aucune action `kind: 'add'` tenue par le socle. */
  sansAjout: "Cette porte ne declare pas d'action d'ajout.",
} as const;

/** Une valeur a ranger dans la fiche vide, par son CHEMIN DE STOCKAGE. */
export interface ValeurDeDepart {
  /** `name`, `status`, `customFields.category`... */
  chemin: string;
  valeur: unknown;
  /**
   * CE QUE LA CASE VERROUILLEE AFFICHE quand la valeur est un ID (le champ du
   * lien impose par le « + Ajouter » d'une carte liee). IL NE SE STOCKE PAS, il
   * s'AFFICHE : « un lien stocke un id, jamais un libelle recopie ».
   *
   * POURQUOI IL NE SUFFIT PAS DE LAISSER FAIRE L'ANNUAIRE. Sur une vraie fiche
   * parente, l'annuaire resout l'id en nom et la case dit « Sony FX9 n°1 » ;
   * depuis un MODELE, l'id impose est celui d'un MODELE, qu'aucun annuaire ne
   * contient (aucune fiche ne le porte). La case afficherait donc l'id nu,
   * attenue, avec « Supprimee ou introuvable » au survol : le pire mot possible
   * sur une valeur parfaitement volontaire. Absent, la case retombe sur ce que
   * la projection en a fait.
   */
  libelle?: string;
}

/**
 * LE JETON QUI DESIGNE LA PERSONNE CONNECTEE, dans `creation.defauts` d'une
 * porte (`"socle:workerId": "socle:personneConnectee"`).
 *
 * UNE LISTE FERMEE, validee par le schema d'ecran (`JETONS_DU_SOCLE`,
 * `packages/data-model/src/app-screens.ts`) et redite ici parce que ce package
 * ne depend pas du modele de donnees (voir l'en-tete de `views/fields.ts`). Pas
 * de jeton pour l'adresse ni pour l'uid : une case qui designe quelqu'un stocke
 * son `personId`, et rouvrir l'adresse serait rouvrir le texte libre.
 */
export const JETON_PERSONNE_CONNECTEE = 'socle:personneConnectee';

/** Tous les jetons de creation que le socle sait resoudre. */
const JETONS_DE_CREATION: ReadonlySet<string> = new Set([JETON_PERSONNE_CONNECTEE]);

/** Cette valeur de defaut est-elle un jeton du socle, a resoudre a l'ouverture ? */
export function estJetonDeCreation(valeur: unknown): boolean {
  return typeof valeur === 'string' && JETONS_DE_CREATION.has(valeur);
}

/**
 * LES DEFAUTS D'UNE PORTE, JETONS RESOLUS : ce que `ficheVide` recoit.
 *
 * UN JETON NE S'ECRIT JAMAIS TEL QUEL dans une fiche : il est remplace par sa
 * valeur au moment ou le brouillon s'ouvre (la personne connectee d'alors). Un
 * jeton SANS REPONSE (pas de port des personnes, pas de fiche membre) est
 * RETIRE : la case reste vide, et si elle est requise « Creer » dit ce qui
 * manque. Ecrire la chaine du jeton creerait une fiche qui designe
 * « socle:personneConnectee », c'est-a-dire personne.
 *
 * L'ORDRE EST GARDE, et les valeurs fixes passent telles quelles.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : un test la tient sans React ni SDK.
 */
export function resoudreDefauts(
  defauts: readonly ValeurDeDepart[],
  contexte: { personneConnectee: string | null },
): ValeurDeDepart[] {
  const sortie: ValeurDeDepart[] = [];
  for (const defaut of defauts) {
    if (!estJetonDeCreation(defaut.valeur)) {
      sortie.push(defaut);
      continue;
    }
    if (defaut.valeur === JETON_PERSONNE_CONNECTEE) {
      const personne = contexte.personneConnectee;
      if (personne !== null && personne !== '') sortie.push({ ...defaut, valeur: personne });
    }
  }
  return sortie;
}

/**
 * LES CHAMPS D'UN MODELE, EN VALEURS DE DEPART : une par clef racine, une par
 * clef du sac (`customFields.<clef>`).
 *
 * POURQUOI LES DEPLIER AU LIEU DE LES RECOPIER D'UN BLOC. Le brouillon issu
 * d'un modele (le menu de « Ajouter », 23/09/2026) superpose TROIS couches : les
 * defauts de la porte, les champs du modele, les valeurs imposees par le geste.
 * Recopier `fields` d'un bloc remplacerait le SAC entier : un defaut de porte
 * rangee dans `customFields` disparaitrait sous le sac du modele, sans erreur.
 * Deplies en chemins, les champs passent par la MEME boucle que les deux autres
 * couches, et le sac se fusionne clef par clef.
 *
 * CE QUI N'EST PAS UN SAC N'EN DEVIENT PAS UN : un `customFields` qui ne serait
 * pas un objet (une saisie de console) est ignore plutot que pose tel quel ; une
 * valeur `undefined` est sautee (elle ne dit rien). `id` et `type` sortent comme
 * les autres clefs, et c'est `ficheVide` qui les fait perdre.
 */
export function valeursDuModele(fields: Record<string, unknown> | undefined): ValeurDeDepart[] {
  if (fields === undefined) return [];
  const valeurs: ValeurDeDepart[] = [];
  for (const [clef, valeur] of Object.entries(fields)) {
    if (valeur === undefined) continue;
    if (clef !== 'customFields') {
      valeurs.push({ chemin: clef, valeur });
      continue;
    }
    if (typeof valeur !== 'object' || valeur === null || Array.isArray(valeur)) continue;
    for (const [clefDuSac, valeurDuSac] of Object.entries(valeur as Record<string, unknown>)) {
      if (valeurDuSac === undefined) continue;
      valeurs.push({ chemin: `${SAC}${clefDuSac}`, valeur: valeurDuSac });
    }
  }
  return valeurs;
}

/**
 * LA FICHE VIDE : ce que le brouillon projette avant la premiere saisie.
 *
 * L'ORDRE EST LE FOND DE CETTE FONCTION : les DEFAUTS de la porte d'abord, puis
 * les champs du MODELE quand le brouillon en vient (le menu de « Ajouter »),
 * puis les valeurs IMPOSEES par le geste (le « + Ajouter » d'une carte liee
 * impose le champ du lien), puis `id` et `type` EN DERNIER, donc ils gagnent
 * toujours. Une valeur par defaut qui s'appellerait `id` ne peut pas usurper le
 * brouillon, et le sous-type est celui de la porte, jamais celui d'un fichier.
 *
 * LE MODELE BAT LES DEFAUTS, ET LES DEFAUTS RESTENT. Un defaut dit ce que la
 * porte propose quand personne n'a rien dit ; un modele, lui, DIT quelque chose
 * (« Disponible », « Sony »), et c'est pour ca qu'on l'a choisi. Mais la fiche
 * issue d'un modele reste la fiche vide de « + Ajouter », pre-remplie (fondateur,
 * 23/09/2026) : ce que le modele ne dit pas garde le defaut de la porte.
 *
 * UN CHEMIN PREFIXE PAR LE SAC VA DANS LE SAC : `customFields.category` ecrit
 * `customFields: { category }`. Le poser a la racine passerait la validation,
 * creerait la fiche, et la rendrait invisible dans toutes les cartes liees,
 * sans la moindre erreur (le meme piege que `appliquerValeursImposees`).
 *
 * FONCTION PURE ET EXPORTEE EXPRES : c'est la seule regle du brouillon qui
 * puisse se tromper en silence (une valeur rangee au mauvais etage), et un test
 * ne la tient que si elle est atteignable sans React et sans SDK.
 */
export function ficheVide(args: {
  type: string | undefined;
  defauts: readonly ValeurDeDepart[];
  /**
   * Les `fields` du modele choisi dans le menu de « Ajouter », tels que le port
   * les relaie. Absent : la fiche vide ordinaire.
   */
  modele?: Record<string, unknown>;
  imposees: readonly ValeurDeDepart[];
}): Record<string, unknown> {
  const fiche: Record<string, unknown> = {};
  for (const { chemin, valeur } of [
    ...args.defauts,
    ...valeursDuModele(args.modele),
    ...args.imposees,
  ]) {
    if (!chemin.startsWith(SAC)) {
      fiche[chemin] = valeur;
      continue;
    }
    const precedent = fiche['customFields'];
    const sac =
      typeof precedent === 'object' && precedent !== null && !Array.isArray(precedent)
        ? (precedent as Record<string, unknown>)
        : {};
    fiche['customFields'] = { ...sac, [chemin.slice(SAC.length)]: valeur };
  }
  fiche['id'] = BROUILLON_ID;
  if (args.type !== undefined) fiche['type'] = args.type;
  return fiche;
}

/**
 * LE TITRE D'UN BROUILLON ISSU D'UN MODELE : le NOM du modele, pose au CHEMIN
 * du role `title`, seulement si la case y est vide (absente, `null`, chaine
 * blanche).
 *
 * POURQUOI. Un modele fabrique a l'ecran ne porte pas son titre dans ses champs
 * (`modeleDuBrouillon` retire le chemin du titre : le titre saisi devient le
 * NOM du modele). A l'instanciation, le SDK comble donc avec ce nom
 * (`avecNomDeSecours`). Le brouillon doit montrer EXACTEMENT ce que la fiche
 * portera : sans cette regle, la case du titre serait vide, « Creer » dirait
 * « Il manque : Nom », et la fiche d'hier, creee en un clic, s'appelait pourtant
 * « Camera de tournage ». Decision du fondateur, 23/09/2026 : le titre reste
 * celui du modele (deux fiches peuvent porter le meme nom, l'id les distingue).
 *
 * UN TITRE DEJA PORTE GAGNE : un modele tire d'une vraie fiche garde son `name`,
 * et c'est lui qu'on montre. `cheminDuTitre` absent ou vide (un titre compose ou
 * calcule, qu'on ne saisit pas) : la fiche part telle quelle, et le SDK
 * comblera cote serveur comme avant.
 */
export function avecTitreDuModele(
  fiche: Record<string, unknown>,
  cheminDuTitre: string | undefined,
  nom: string,
): Record<string, unknown> {
  if (cheminDuTitre === undefined || cheminDuTitre === '') return fiche;
  const lu = lireChemin(fiche, cheminDuTitre);
  if (lu !== null && lu !== undefined && String(lu).trim() !== '') return fiche;
  return poserChemin(fiche, cheminDuTitre, nom);
}

/**
 * CE QUI PART AU PORT : la fiche sans `id` (celui du brouillon n'est pas un id)
 * ni `type` (il voyage a part, c'est la porte qui le connait). Tout le reste
 * part TEL QUEL, sac compris : c'est le SDK qui valide.
 */
export function champsDuBrouillon(fiche: Record<string, unknown>): Record<string, unknown> {
  const { id: _id, type: _type, ...champs } = fiche;
  return champs;
}

/** Une case VIDE : rien, `null`, ou une chaine sans un caractere. */
const vide = (valeur: Cell | undefined): boolean =>
  valeur === null || valeur === undefined || String(valeur).trim() === '';

/**
 * LES CASES OBLIGATOIRES ENCORE VIDES, dans l'ordre ou la fiche les montre :
 * le TITRE d'abord (une fiche sans titre n'existe pour personne), puis les
 * colonnes que le fichier de champs declare `required`.
 *
 * Deux listes de meme longueur, alignees : les CLES (la convention de la fiche,
 * `role:title`, `col:<clef>`, pour cercler les cases) et les LIBELLES (pour la
 * phrase). Vides toutes les deux quand rien ne manque.
 *
 * `titre: undefined` veut dire « pas de role titre a verifier » (un titre
 * calcule par une fonction, qu'on ne peut pas saisir) : on ne reclame pas ce
 * qu'on ne peut pas taper.
 */
export function manquantsDuBrouillon(args: {
  titre: { valeur: Cell; libelle: string } | undefined;
  colonnes: readonly { key: string; label: string; required?: boolean; valeur: Cell }[];
}): { cles: string[]; libelles: string[] } {
  const cles: string[] = [];
  const libelles: string[] = [];
  if (args.titre !== undefined && vide(args.titre.valeur)) {
    cles.push('role:title');
    libelles.push(args.titre.libelle);
  }
  for (const colonne of args.colonnes) {
    if (colonne.required !== true || !vide(colonne.valeur)) continue;
    cles.push(`col:${colonne.key}`);
    libelles.push(colonne.label);
  }
  return { cles, libelles };
}

/**
 * LA PHRASE DU REFUS, une seule dans le produit : « Il manque : Nom, Categorie. »
 * Elle se pose dans la pastille de la rangee 3 (`SaveState.reason`), donc sans
 * bouton « Reessayer » : rejouer le meme clic sans rien saisir redirait la
 * meme chose.
 */
export function phraseDesManquants(libelles: readonly string[]): string {
  return `Il manque : ${libelles.join(', ')}.`;
}

/**
 * LE MOT QUI ANNONCE UN EXEMPLE, ecrit ICI et nulle part ailleurs.
 *
 * POURQUOI LE SOCLE L'AJOUTE, ET NON LA DECLARATION. Une case grise qui dit
 * « PXW-FX9 » se lit comme une valeur DEJA POSEE : on croit le champ rempli, on
 * passe au suivant, et la fiche naît sans son modele. « par ex. PXW-FX9 » dit
 * ce que la case attend au lieu de le montrer. Le fichier de champs, lui, garde
 * l'exemple NU (`"placeholder": "PXW-FX9"`) : c'est ce qu'on saisit dans le
 * Studio, ce qu'on relit, et ce qu'on traduira le jour ou l'interface parlera
 * une autre langue. Le mot appartient au produit, la valeur appartient a l'app.
 *
 * Regle du fondateur, 09/09/2026.
 */
export const PREFIXE_EXEMPLE = 'par ex. ';

/**
 * L'exemple declare, prêt a s'afficher, ou `undefined` quand il n'y en a pas
 * (l'appelant retombe alors sur `placeholderParDefaut`, qui n'est pas un
 * exemple mais une consigne : « Ecrire ici », « Choisir »).
 */
export function phraseDExemple(declare: string | undefined): string | undefined {
  if (declare === undefined) return undefined;
  const nu = declare.trim();
  return nu === '' ? undefined : `${PREFIXE_EXEMPLE}${nu}`;
}

/**
 * LE PLACEHOLDER D'UNE CASE VIDE DU BROUILLON quand rien n'est declare, par
 * type d'editeur. « Ecrire ici » plutot que « Non renseigne » : sur une fiche
 * qui n'existe pas encore, rien n'a ete renseigne, tout est a ecrire, et le
 * mot doit inviter. Une date se dit « Choisir une date » en lecture (l'editeur
 * natif rend deja `jj/mm/aaaa` a l'ouverture).
 */
export function placeholderParDefaut(editeur: string | undefined): string {
  switch (editeur) {
    case 'select':
    case 'lien':
      return 'Choisir';
    case 'date':
      return 'Choisir une date';
    case 'datetime':
      return 'Choisir une date et une heure';
    case 'number':
      return '0';
    default:
      return 'Ecrire ici';
  }
}

/** Le placeholder du titre d'un brouillon quand la declaration n'en donne pas. */
export const PLACEHOLDER_TITRE = 'Titre de la fiche';
